import type { ReactNode } from "react"

// The set of HUD metrics that have a details overlay. `labor` is the shared
// box opened from the AI / Human / Robot labor-mix legend entries.
export type MetricId =
  | "labor"
  | "income"
  | "employment"
  | "safetyTax"
  | "alignment"
  | "capability"
  | "power"

// The two after-split branches. A = Plan A (the focal line the now-dot rides),
// D = Default path (the red comparison). Short keys because the series /
// scenarios / end-labels all key off them.
export type BranchKey = "A" | "D"

// The plans drawn as comparison lines against Plan A, all sourced from the
// model spreadsheet's per-plan tabs (planBranchSeries).
export type ComparisonPlanId = "D" | "B" | "C" | "S"

// The year every metric's projection forks into the Plan A / Default branches.
// Shared by the per-metric configs (metricDetailsData), the capability series
// sampling, and the chart, so a model fork-year change is a one-line edit.
export const METRIC_FORK_YEAR = 2029

// The last year on the explainer charts' x-axis — Plan A's data end. Shared by
// the per-metric xTicks and by scripts/extractPlanBranchSeries.ts (which
// truncates a longer comparison series, e.g. Plan S to 2045, at this year so
// it can't stretch the axis past Plan A).
export const CHART_MAX_YEAR = 2040

export type SeriesPoint = { year: number; value: number }

// A metric's trajectory as a shared trunk that forks: `observed` (thick solid,
// real data) runs into `projection` (the shared, dotted projection); at the
// split year the `branches` begin, each sharing the projection's final point.
export type MetricScenarioSeries = {
  observed: SeriesPoint[]
  projection: SeriesPoint[]
  branches: Record<BranchKey, SeriesPoint[]>
}

// How the auto-derived value labels (the projection-end figure at the split) are
// formatted; axis tick labels are authored directly. Defaults preserve the
// original behaviour: linear→"percent", log→"currency".
export type AxisValueUnit = "percent" | "currency" | "count" | "multiplier" | "years" | "humanEquiv"

// Y-axis: `linear` metrics run 0..`max` (default 1) with an optional top label;
// share metrics stay at max 1, other scalar metrics set an explicit max.
// `log` metrics (income, alignment, capability, power) map a decade range with
// explicit tick labels.
export type MetricAxis =
  | { type: "linear"; topLabel?: string; max?: number; unit?: AxisValueUnit }
  | { type: "log"; min: number; max: number; ticks: { value: number; label: string }[]; unit?: AxisValueUnit }

export type MetricDetail = {
  id: MetricId
  label: string
  // Optional explanatory prose shown under the chart. Omit to drop that section
  // entirely (e.g. where the mechanism lives in the scenario rows instead).
  explanation?: ReactNode
  // One line per branch, keyed by branch. Rendered in branch order A, D.
  scenarios: Record<BranchKey, ReactNode>
  // End-of-line value label for Plan A (the only line whose value is printed
  // on the chart — comparison lines carry letter tags, values on hover).
  // Optional override: by default the chart derives the label from the
  // branch's last point via fmtValue, so live-sourced series can't drift from
  // their labels. Author one only where the derived format is wrong (e.g.
  // "~9", "1%").
  endLabels?: Partial<Record<BranchKey, string>>
  // Supplement slugs; non-supplement provenance stays in the prose (filtered
  // through SUPPLEMENT_SLUGS at render).
  sources: string[]
  series: MetricScenarioSeries
  axis: MetricAxis
  // Years labelled on the x-axis — the points where the modelling changes.
  xTicks: number[]
  // Year the branches diverge (where the projection ends and A/D fork).
  splitYear: number
}
